<template>
  <div id="product-image">
    <input
      :id="`image-input-${imagePosition}`"
      :disabled="viewModeOnly"
      accept="image/png,image/x-png,image/jpeg,image/webp,image/avif,.png,.jpg,.jpeg,.webp,.avif"
      ref="fileUploader"
      style="display: none"
      type="file"
      @change="setImage($event)"
    />
    <div class="p-image-wrapper elevation-1">
      <v-img
        contain
        width="100%"
        height="100%"
        :src="imageUrl"
        @error="imageError"
      ></v-img>

      <div
        :class="{ show: !imageUrl, 'view-mode-only': viewModeOnly }"
        class="d-flex flex-column justify-space-between p-image-overlay pa-2 pa-md-4"
      >
        <div
          class="px-2 py-1 text-center g-border-radius-s p-tag"
          :class="{
            orange: isMainImage,
            primary: !isMainImage
          }"
        >
          <p v-if="isMainImage">Mandatory *</p>
          <p v-else>Optional</p>
        </div>

        <div class="mb-0 text-center p-text">
          <p v-if="isMainImage">
            Must not include multiple views of a single product
          </p>
          <p v-if="isLeftImage">Side or other image</p>
          <p v-if="isBackImage">Back or other image</p>
          <p v-if="isNutritionalPanelImage">Nutritional Panel or other image</p>
        </div>

        <div v-if="!viewModeOnly">
          <template v-if="isMainImage">
            <v-sheet
              class="p-sheet-add-image"
              :for="`image-input-${imagePosition}`"
            >
              <label :for="`image-input-${imagePosition}`">
                <div class="g-full-width p-div-btn-secondary elevation-1">
                  {{ imageUrl ? 'Update image' : 'Add image' }}
                </div>
              </label>
            </v-sheet>
          </template>
          <template v-else>
            <div>
              <v-sheet
                class="p-sheet-add-image"
                :for="`image-input-${imagePosition}`"
              >
                <label :for="`image-input-${imagePosition}`">
                  <div class="g-full-width p-div-btn-secondary elevation-1">
                    {{ imageUrl ? 'Update image' : 'Add image' }}
                  </div>
                </label>
              </v-sheet>
              <v-btn
                v-if="imageUrl"
                rounded
                class="mt-2 mt-md-3 g-full-width i-btn-block-red"
                @click="removeImage"
                >Remove Image</v-btn
              >
            </div>
          </template>
        </div>

        <div v-else>
          <p class="mb-0 text-overlay text-center">View mode</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { ImageValidationHelper } from '@/components/helpers/ImageValidationHelper'

const MAX_ALLOWED_IMAGE_SIZE = 1000000
const MAX_ALLOWED_IMAGE_NAME_LENGTH = 200
export default {
  props: {
    imageUrl: {
      default: '',
      type: String
    },
    imagePosition: {
      default: 'MAIN',
      type: String
    },
    imagePositions: {
      type: Object
    },
    viewModeOnly: {
      default: false,
      type: Boolean
    }
  },

  computed: {
    isMainImage() {
      return this.imagePosition === this.imagePositions.MAIN
    },

    isLeftImage() {
      return this.imagePosition === this.imagePositions.LEFT
    },

    isBackImage() {
      return this.imagePosition === this.imagePositions.BACK
    },

    isNutritionalPanelImage() {
      return this.imagePosition === this.imagePositions.NUTRITIONAL_PANEL
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    displayErrorNotification(errorMessage) {
      this.addNotification({
        color: 'error',
        text: errorMessage
      })
    },

    imageError(e) {
      this.$emit('imageError', this.imagePosition, e)
    },

    removeImage() {
      this.$refs.fileUploader.value = ''
      this.$emit('removeImage', this.imagePosition)
    },

    setImage(event) {
      if (!event?.target?.files || event.target.files.length < 1) return

      const validSize = ImageValidationHelper.isValidImageSize(
        event.target.files[0].size,
        MAX_ALLOWED_IMAGE_SIZE
      )
      const validFormat = ImageValidationHelper.isValidImageFormat(
        event.target.files[0].name
      )
      const validName = ImageValidationHelper.isValidImageName(
        event.target.files[0].name
      )
      const validNameLength = ImageValidationHelper.isValidImageNameLength(
        event.target.files[0].name,
        MAX_ALLOWED_IMAGE_NAME_LENGTH
      )

      if (!validSize)
        this.displayErrorNotification('Image file size is larger than 1MB...')
      else if (!validFormat)
        this.displayErrorNotification(
          'Image file must be in JPEG (.jpeg/.jpg), PNG (.png), WEBP (.webp), or AVIF (.avif) format only...'
        )
      else if (!validName)
        this.displayErrorNotification(
          'Image file name allows special characters "space", "_" and "-" only...'
        )
      else if (!validNameLength)
        this.displayErrorNotification(
          `Image file name can be up to ${MAX_ALLOWED_IMAGE_NAME_LENGTH} characters only...`
        )
      else {
        const newImage = event.target.files[0]
        // URL locally created to display image in component
        const newImageUrl = URL.createObjectURL(newImage)
        this.$emit('newImage', this.imagePosition, newImageUrl, newImage)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#product-image {
  .p-image-wrapper {
    height: 220px;
    width: 100%;
    position: relative;
    margin: auto;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    overflow: hidden;
    .p-image-overlay {
      background-color: rgba(0, 0, 0, 0.6);
      opacity: 0;
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      transition: opacity 0.3s ease-in-out;
      &:hover,
      &.show,
      &.view-mode-only {
        opacity: 1;
      }
      &.view-mode-only {
        cursor: default !important;
      }
      .p-tag {
        display: inline-block;
        font-weight: bold;
        letter-spacing: 0.25px;
        min-width: 108px;
        p {
          font-size: 12px;
        }
        &.orange {
          background: #ff9800;
          color: white;
        }
        &.primary {
          background: $black-color;
          color: white;
        }
      }
      .p-text {
        font-size: 10px !important;
        letter-spacing: 0.2px;
      }
      .p-tag,
      .p-text {
        color: white;
      }
      .p-sheet-add-image {
        background-color: rgba(0, 0, 0, 0);
      }
      .p-div-btn-secondary {
        text-align: center;
        font-weight: bold;
        color: white;
        background-color: $secondary-color;
        font-weight: bold;
        letter-spacing: 0.75px;
        padding: 6px 0px 6px 0px;
        border-radius: 50px;
        &:hover {
          cursor: pointer;
        }
      }
    }
  }
}

@media only screen and (min-width: 600px) {
  #product-image {
    .p-image-wrapper {
      height: 300px;
      .p-image-overlay {
        .p-tag {
          p {
            font-size: 16px;
          }
        }
        .p-text {
          font-size: 12px !important;
          letter-spacing: 0.35px;
        }
      }
    }
  }
}

@media only screen and (min-width: 960px) {
  #product-image {
    .p-image-wrapper {
      .p-image-overlay {
        .p-tag {
          p {
            font-size: 18px;
          }
        }
        .p-text {
          font-size: 13px !important;
        }
      }
    }
  }
}
</style>
